import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, X, Info, LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface PageHeaderCardProps {
  /** Small uppercase label above the title */
  eyebrow?: string;
  /** Main title (large) */
  title: ReactNode;
  /** Optional subtitle text shown under the title */
  subtitle?: string;
  /** Optional decorative note shown in its own row with an icon */
  note?: string;
  /** Icon used for the note row (defaults to Info) */
  subtitleIcon?: LucideIcon;
  /** Back link target (defaults to /home). Pass null to hide. Ignored if onBack is provided. */
  backTo?: string | null;
  /** Optional back click handler; when provided, renders a button instead of a Link. */
  onBack?: () => void;
  /** Top-right slot (primary actions / view toggle) */
  rightTop?: ReactNode;
  /** Search input config */
  search?: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
  };
  /** Trailing slot on the search row (filters, secondary tools) */
  tools?: ReactNode;
  /** Visual density used by builder/editor pages */
  variant?: "card" | "flat";
}

export function PageHeaderCard({
  eyebrow,
  title,
  subtitle,
  note,
  subtitleIcon: SubtitleIcon = Info,
  backTo = "/home",
  onBack,
  rightTop,
  search,
  tools,
  variant = "card",
}: PageHeaderCardProps) {
  const hasBottomRow = !!search || !!tools;
  const isFlat = variant === "flat";
  const backBtnClass = cn(
    "mt-1 rounded-xl border border-border text-muted-foreground transition-all group shrink-0",
    isFlat
      ? "p-2.5 bg-card shadow-[0_8px_22px_-16px_hsl(var(--anamnese-text)/0.35)] hover:text-anamnese-violet hover:border-anamnese-border hover:bg-anamnese-violet-faint"
      : "p-2.5 hover:text-primary hover:border-primary/30 hover:bg-primary/5"
  );

  return (
    <section
      className={cn(
        "relative overflow-hidden text-foreground",
        isFlat
          ? "rounded-none border-0 bg-transparent shadow-none"
          : "rounded-3xl border border-border bg-card shadow-[0_20px_70px_-15px_hsl(var(--primary)/0.12)]"
      )}
    >
      {/* Soft decorative glow */}
      {!isFlat && (
        <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
      )}

      <div className={cn("relative", isFlat ? "px-0 py-0" : "p-6 sm:p-8 md:p-10")}>
        {/* Top row: back + titles + right slot */}
        <div className={cn("flex flex-col md:flex-row md:justify-between", isFlat ? "gap-4 md:items-start" : "gap-6 md:items-end")}>
          <div className="flex items-start gap-4 sm:gap-5 min-w-0">
            {onBack ? (
              <button type="button" onClick={onBack} className={backBtnClass} aria-label="Voltar">
                <ArrowLeft className="h-5 w-5 group-hover:-translate-x-0.5 transition-transform" />
              </button>
            ) : backTo ? (
              <Link to={backTo} className={backBtnClass} aria-label="Voltar">
                <ArrowLeft className="h-5 w-5 group-hover:-translate-x-0.5 transition-transform" />
              </Link>
            ) : null}
            <div className="min-w-0">
              {eyebrow && (
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary mb-1 block">
                  {eyebrow}
                </span>
              )}
              <h1 className={cn("font-bold tracking-tight", isFlat ? "text-[21px] leading-7 text-anamnese-text" : "text-3xl sm:text-4xl text-foreground")}>
                {title}
              </h1>
              {subtitle && (
                <p className={cn("text-muted-foreground font-medium max-w-2xl", isFlat ? "mt-0.5 text-[13px] text-anamnese-muted" : "mt-2 text-sm")}>
                  {subtitle}
                </p>
              )}
              {note && (
                <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <SubtitleIcon className="h-3.5 w-3.5 text-primary/70 shrink-0" />
                  <span>{note}</span>
                </p>
              )}
            </div>
          </div>

          {rightTop && (
            <div className="flex items-center gap-3 shrink-0">{rightTop}</div>
          )}
        </div>

        {/* Search + tools */}
        {hasBottomRow && (
          <>
            <div className="h-px w-full bg-border/70 my-6 sm:my-8" />
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              {search && (
                <div className="relative flex-1 group">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors pointer-events-none" />
                  <Input
                    placeholder={search.placeholder || "Buscar..."}
                    value={search.value}
                    onChange={(e) => search.onChange(e.target.value)}
                    className="pl-12 pr-10 h-12 rounded-2xl bg-muted/40 border-border focus-visible:ring-4 focus-visible:ring-primary/10 focus-visible:border-primary/40"
                  />
                  {search.value && (
                    <button
                      type="button"
                      onClick={() => search.onChange("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label="Limpar busca"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
              {tools && (
                <div className="flex items-center gap-2 sm:ml-auto overflow-x-auto scrollbar-none">
                  {tools}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
